import { useMutation, useQueryClient } from '@tanstack/react-query';
import { memo } from 'react';
import { z } from 'zod';

import { $$toast } from '@doger/ui/components/ui/toast';
import { useAppForm } from '@doger/ui/form';

import { $$privateApi } from '@doger/admin/shared/api';

import styles from './add-note-form.module.scss';

type AddNoteFormProps = {
  userId: string;
  onSubmit: () => void;
  onCancel: () => void;
};

const addNoteSchema = z.object({
  content: z
    .string()
    .min(1, 'Note content is required')
    .min(3, 'Note must be at least 3 characters long')
    .max(1000, 'Note must be less than 1000 characters'),
});

export const AddNoteForm = memo<AddNoteFormProps>(
  ({ userId, onSubmit, onCancel }) => {
    const queryClient = useQueryClient();

    const createNoteMutation = useMutation({
      mutationFn: ({ content }: { content: string }) =>
        $$privateApi.createNote({ userId, content }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user-notes', userId] });
        $$toast.success('Note added successfully');
        onSubmit();
      },
    });

    const form = useAppForm({
      defaultValues: {
        content: '',
      },
      validators: {
        onSubmit: addNoteSchema,
        onChange: addNoteSchema,
      },
      onSubmit: async ({ value }) => {
        await createNoteMutation.mutateAsync({ content: value.content });
      },
    });

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className={styles.form}
      >
        <form.AppForm>
          <form.AppField name='content'>
            {(field) => (
              <field.TextAreaField
                className={styles.textarea}
                label='Note'
                placeholder='Enter your note about this user...'
                rows={6}
              />
            )}
          </form.AppField>
        </form.AppForm>

        <div className={styles.actions}>
          <form.AppForm>
            <form.ResetButton
              variant='outlined'
              palette='ghost'
              size='md'
              onClick={onCancel}
            >
              {() => 'Cancel'}
            </form.ResetButton>
          </form.AppForm>

          <form.AppForm>
            <form.SubmitButton variant='contained' size='md' palette='primary'>
              {({ isSubmitting }) => (isSubmitting ? 'Adding...' : 'Add Note')}
            </form.SubmitButton>
          </form.AppForm>
        </div>
      </form>
    );
  }
);
